import { Document } from "@components/documents/types";
import { CURRENT_DOC_ITEM } from "@constants/documents";
import { OnSelectionChangeData } from "@fluentui/react-components";
import { useDocumentsQuery } from "@hooks/useDocuments";
import { useSessions } from "@hooks/useSessions";
import { useWorkingDocumentId, type WorkingDocumentId } from "@hooks/useWorkingDocumentId";
import { dedupeBy } from "@utils/index";
import { useCallback, useMemo } from "react";
import useSessionStorageState from "use-session-storage-state";

type SessionId = string;

// Named only for parity with the other *_STORAGE_KEY consts. Single use, so this is
// otherwise an early refactor — that's why one-off keys are left inline elsewhere.
const DOCUMENT_CONTEXT_MAP_STORAGE_KEY = "documentContextMap";

export type UseDocumentContextResult = {
  contextDocuments: Document[];
  availableDocuments: Document[];
  addContextDocument: (doc: Document) => void;
  updateContextDocument: (docId: string, updates: Partial<Document>) => void;
  removeContextDocument: (docId: string) => void;
  seedContextDocuments: (docs: Document[], targetSessionId: string) => void;
  handleSelectionChange: (data: OnSelectionChangeData, allDocuments: Document[]) => void;
};

export const useDocumentContext = (): UseDocumentContextResult => {
  const { documentId: workingDocumentId } = useWorkingDocumentId();
  const { sessionId } = useSessions();
  const { data: documents = [] } = useDocumentsQuery();

  // storageSync off keeps context state isolated to this taskpane instance.
  const [contextMap, setContextMap] = useSessionStorageState<Record<WorkingDocumentId, Record<SessionId, Document[]>>>(
    DOCUMENT_CONTEXT_MAP_STORAGE_KEY,
    { defaultValue: {}, storageSync: false }
  );

  const contextDocuments = useMemo(() => {
    if (!workingDocumentId || !sessionId) return [];
    return contextMap[workingDocumentId]?.[sessionId] ?? [CURRENT_DOC_ITEM];
  }, [contextMap, workingDocumentId, sessionId]);

  const setContextDocuments = useCallback(
    (updater: (prev: Document[]) => Document[], targetSessionId: string | null) => {
      if (!workingDocumentId || !targetSessionId) return;

      setContextMap((prev = {}) => {
        const currentContext = prev[workingDocumentId]?.[targetSessionId];
        const newContext = updater(currentContext ?? [CURRENT_DOC_ITEM]);

        return {
          ...prev,
          [workingDocumentId]: {
            ...(prev[workingDocumentId] ?? {}),
            [targetSessionId]: newContext,
          },
        };
      });
    },
    [workingDocumentId, setContextMap]
  );

  const availableDocuments = useMemo(() => {
    return [CURRENT_DOC_ITEM, ...documents];
  }, [documents]);

  const addContextDocument = useCallback(
    (doc: Document) => {
      setContextDocuments((prev) => {
        if (prev.some((cd) => cd.id === doc.id)) {
          return prev;
        }
        return [...prev, doc];
      }, sessionId);
    },
    [setContextDocuments, sessionId]
  );

  const updateContextDocument = useCallback(
    (docId: string, updates: Partial<Document>) => {
      setContextDocuments((prev) => prev.map((doc) => (doc.id === docId ? { ...doc, ...updates } : doc)), sessionId);
    },
    [setContextDocuments, sessionId]
  );

  // Explicit sessionId because the session creation flow seeds right after
  // creating the session, before `setCurrentSession` runs — the hook's
  // `sessionId` still reflects the previous session at that moment.
  const seedContextDocuments = useCallback(
    (docs: Document[], targetSessionId: string) => {
      setContextDocuments((prev) => dedupeBy([...prev, ...docs], "id"), targetSessionId);
    },
    [setContextDocuments]
  );

  const removeContextDocument = useCallback(
    (docId: string) => {
      setContextDocuments((prev) => prev.filter((doc) => doc.id !== docId), sessionId);
    },
    [setContextDocuments, sessionId]
  );

  const handleSelectionChange = useCallback(
    (data: OnSelectionChangeData, allDocuments: Document[]) => {
      const newSelectedIds = data.selectedItems as Set<string>;
      const currentIds = new Set(contextDocuments.map((d) => d.id));

      newSelectedIds.forEach((id) => {
        if (!currentIds.has(id)) {
          const doc = allDocuments.find((d) => d.id === id);
          if (doc) addContextDocument(doc);
        }
      });

      currentIds.forEach((id) => {
        if (!newSelectedIds.has(id)) {
          removeContextDocument(id);
        }
      });
    },
    [contextDocuments, addContextDocument, removeContextDocument]
  );

  return {
    contextDocuments,
    availableDocuments,
    addContextDocument,
    updateContextDocument,
    removeContextDocument,
    seedContextDocuments,
    handleSelectionChange,
  };
};
